import React, { useEffect, useRef, useState } from "react";

/** @jsx jsx */
import { jsx, css, keyframes } from '@emotion/react';

import { Colors } from "../palettes/colorPalette";
import { GameClient } from "../gameClient";
import { getSystem } from "../../../shared/utils";
import { PlacementSystem } from "../systems/placementSystem";
import { backgroundTexture } from "../uiConstants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { UIConstants } from "../../../shared/constants";

declare var gameClient: GameClient;

interface ModalProps {
  isOpen: boolean;
  fullScreen?: boolean;
  noBackground?: boolean;
  hasCloseBtn?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  height?: string;
  width?: string;
  zIndex?: number;
};

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fullScreenStyle = css`
  top: 0;
  transform: translateY(0%);
  border-radius: 0;
  border: none;
  max-width: 100vw;
  max-height: 100vh;
  height: inherit;
  width: auto;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  animation: ${fadeIn} ${UIConstants.ModalFadeIn}s ease-in-out;
`;

const modalStyle = css`
  top: 50%;
  transform: translateY(-50%);
  border-radius: 15px;
  max-width: 50vw;
  max-height: 100vh;
  user-select: none;
  animation: ${fadeIn} ${UIConstants.ModalFadeIn}s ease-in-out;

`;

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  fullScreen,
  hasCloseBtn = true,
  onClose,
  children,
  noBackground,
  height,
  width,
  zIndex
}) => {
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const modalRef = useRef<HTMLDialogElement | null>(null);

  const onMouse = () => {
    //When the mouse is active over a model let the placement system know to 
    //prevent interaction with entities
    if (gameClient.world !== undefined) {
      const placement = getSystem(gameClient.world, PlacementSystem);
      if (placement) {
        placement.signalUIInterceptThisFrame();
      }
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    setModalOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") {
      handleCloseModal();
    }
  };

  useEffect(() => {
    setModalOpen(() => isOpen);
  }, [isOpen]);

  useEffect(() => {
    const modalElement = modalRef.current;

    if (modalElement) {
      if (isModalOpen) {
        modalElement.show();
      } else {
        modalElement.close();
      }
    }
  }, [isModalOpen]);

  return (
    <>
      {
        isModalOpen && !fullScreen &&
        <div style={{
          visibility: isModalOpen ? 'visible' : 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex,
        }} onClick={!noBackground ? handleCloseModal : undefined}
          onMouseDown={onMouse}
          onMouseUp={onMouse}
          onMouseMove={onMouse}
        />
      }
      <dialog css={fullScreen ? fullScreenStyle : modalStyle} style={{
        background: noBackground ? "" : `url(${backgroundTexture})`,
        backgroundColor: noBackground ? "transparent" : Colors.UIBackground,
        border: noBackground ? '0px solid black' : '5px solid black',
        overflow: noBackground ? 'unset' : 'hidden',
        height: height,
        width: width,
        zIndex,
      }} ref={modalRef}
        onKeyDown={handleKeyDown}
        onMouseDown={onMouse}
        onMouseUp={onMouse}
        onMouseMove={onMouse}
      >
        {hasCloseBtn && (
          <FontAwesomeIcon style={{
            position: 'absolute',
            top: '1vh',
            right: '1vh',
            cursor: 'pointer',
            zIndex: 10,
            color: noBackground ? Colors.UIIncomingTabText : Colors.UITextAndBorders
          }} icon={faTimesCircle} size="2x" onClick={handleCloseModal} />
        )}
        {children}
      </dialog>
    </>
  );
};
